import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
  Chip,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CookiePolicy = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const lastUpdated = 'February 16, 2026';

  const SectionTitle = ({ children }) => (
    <Typography
      variant="h5"
      component="h2"
      sx={{ mt: 4, mb: 2, color: theme.palette.primary.main }}
    >
      {children}
    </Typography>
  );

  const SubSectionTitle = ({ children }) => (
    <Typography
      variant="h6"
      component="h3"
      sx={{ mt: 3, mb: 1.5, color: theme.palette.text.primary }}
    >
      {children}
    </Typography>
  );

  const cookieData = [
    {
      name: 'firebase-auth-token',
      purpose: 'User authentication and session management',
      category: 'Essential',
      duration: 'Session / 1 hour',
      provider: 'Firebase',
    },
    {
      name: '__session',
      purpose: 'Session state management',
      category: 'Essential',
      duration: 'Session',
      provider: 'FamilyForLife',
    },
    {
      name: 'theme-mode',
      purpose: 'Remember user theme preference (light/dark mode)',
      category: 'Functional',
      duration: '1 year',
      provider: 'FamilyForLife',
    },
    {
      name: 'cookie-consent',
      purpose: 'Store cookie consent preferences',
      category: 'Essential',
      duration: '1 year',
      provider: 'FamilyForLife',
    },
    {
      name: '_ga, _ga_*',
      purpose: 'Google Analytics - usage statistics',
      category: 'Analytics',
      duration: '2 years',
      provider: 'Google',
    },
    {
      name: 'firebase-installations-*',
      purpose: 'Firebase installation tracking for app functionality',
      category: 'Functional',
      duration: '180 days',
      provider: 'Firebase',
    },
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Essential':
        return 'primary';
      case 'Functional':
        return 'info';
      case 'Analytics':
        return 'warning';
      case 'Marketing':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Cookie Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            Last Updated: {lastUpdated}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Introduction */}
          <Typography variant="body1" paragraph>
            This Cookie Policy explains how FamilyForLife ("we," "our," or "us") uses
            cookies and similar tracking technologies when you use our family management
            web application (the "Service"). This policy provides information about what
            cookies are, what types we use, and how you can control them.
          </Typography>

          <Typography variant="body1" paragraph>
            By using the Service, you consent to the use of cookies as described in this
            policy. You can manage your cookie preferences at any time through your browser
            settings or our cookie consent tool.
          </Typography>

          {/* Table of Contents */}
          <SectionTitle>Table of Contents</SectionTitle>
          <List dense>
            {[
              'What Are Cookies?',
              'Types of Cookies We Use',
              'Detailed Cookie Information',
              'Third-Party Cookies',
              'Local Storage and Similar Technologies',
              'Managing Your Cookie Preferences',
              'Impact of Disabling Cookies',
              'Do Not Track Signals',
              'Updates to This Policy',
              'Contact Us',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${index + 1}. ${item}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          {/* 1. What Are Cookies? */}
          <SectionTitle>1. What Are Cookies?</SectionTitle>
          <Typography variant="body1" paragraph>
            Cookies are small text files that are placed on your device (computer, tablet,
            or mobile phone) when you visit a website. They are widely used to make websites
            work more efficiently, provide information to website owners, and enhance user
            experience.
          </Typography>
          <Typography variant="body1" paragraph>
            Cookies can be "session cookies" (which are deleted when you close your browser)
            or "persistent cookies" (which remain on your device for a set period or until
            you delete them). Cookies can be "first-party" (set by us) or "third-party"
            (set by other services we use).
          </Typography>

          {/* 2. Types of Cookies We Use */}
          <SectionTitle>2. Types of Cookies We Use</SectionTitle>
          <Typography variant="body1" paragraph>
            We categorize the cookies we use into the following types:
          </Typography>

          <SubSectionTitle>2.1 Essential Cookies</SubSectionTitle>
          <Typography variant="body1" paragraph>
            These cookies are strictly necessary for the Service to function. They enable
            core functionality such as user authentication, security features, and session
            management. The Service cannot work properly without these cookies, and they
            cannot be disabled.
          </Typography>

          <SubSectionTitle>2.2 Functional Cookies</SubSectionTitle>
          <Typography variant="body1" paragraph>
            These cookies enable enhanced functionality and personalization, such as
            remembering your preferences (like theme settings). While not strictly
            necessary, they significantly improve your experience.
          </Typography>

          <SubSectionTitle>2.3 Analytics Cookies</SubSectionTitle>
          <Typography variant="body1" paragraph>
            These cookies help us understand how visitors interact with the Service by
            collecting and reporting information anonymously. We use this data to improve
            the Service and fix issues.
          </Typography>

          <SubSectionTitle>2.4 Marketing Cookies (Currently Not Used)</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We currently do not use marketing or advertising cookies. If this changes
            in the future, we will update this policy and seek your consent before
            setting such cookies.
          </Typography>

          {/* 3. Detailed Cookie Information */}
          <SectionTitle>3. Detailed Cookie Information</SectionTitle>
          <Typography variant="body1" paragraph>
            The following table provides detailed information about the cookies we use:
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ my: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Cookie Name</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Purpose</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cookieData.map((cookie, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {cookie.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cookie.category}
                        size="small"
                        color={getCategoryColor(cookie.category)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{cookie.purpose}</TableCell>
                    <TableCell>{cookie.duration}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 4. Third-Party Cookies */}
          <SectionTitle>4. Third-Party Cookies</SectionTitle>
          <Typography variant="body1" paragraph>
            Some cookies are placed by third-party services that appear on our pages.
            We use the following third-party services that may set cookies:
          </Typography>

          <SubSectionTitle>4.1 Firebase (Google)</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We use Firebase for authentication, database, and hosting services. Firebase
            may set cookies necessary for security and authentication. For more information,
            see{' '}
            <Link href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener">
              Firebase Privacy Policy
            </Link>.
          </Typography>

          <SubSectionTitle>4.2 Google Analytics</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We may use Google Analytics to understand how users interact with our Service.
            Google Analytics uses cookies to collect information about your use of the
            Service, which is transmitted to and stored by Google. You can opt-out using
            the{' '}
            <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener">
              Google Analytics Opt-out Browser Add-on
            </Link>. For more information, see{' '}
            <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">
              Google Privacy Policy
            </Link>.
          </Typography>

          {/* 5. Local Storage and Similar Technologies */}
          <SectionTitle>5. Local Storage and Similar Technologies</SectionTitle>
          <Typography variant="body1" paragraph>
            In addition to cookies, we use other technologies to store information locally
            on your device:
          </Typography>

          <SubSectionTitle>5.1 Local Storage</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We use browser local storage to store:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Theme preferences"
                secondary="Your selected light/dark mode setting"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Cookie consent status"
                secondary="Your cookie preferences"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Application state"
                secondary="Temporary data to improve performance"
              />
            </ListItem>
          </List>

          <SubSectionTitle>5.2 IndexedDB</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Firebase may use IndexedDB to cache authentication tokens and offline data
            for improved performance.
          </Typography>

          {/* 6. Managing Your Cookie Preferences */}
          <SectionTitle>6. Managing Your Cookie Preferences</SectionTitle>
          <Typography variant="body1" paragraph>
            You have several options for managing cookies:
          </Typography>

          <SubSectionTitle>6.1 Cookie Consent Banner</SubSectionTitle>
          <Typography variant="body1" paragraph>
            When you first visit our Service, you will see a cookie consent banner that
            allows you to accept or customize your cookie preferences. You can change
            these preferences at any time by clicking the cookie settings link in the
            footer.
          </Typography>

          <SubSectionTitle>6.2 Browser Settings</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Most browsers allow you to control cookies through their settings:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener">
                    Chrome Cookie Settings
                  </Link>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Link href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener">
                    Firefox Cookie Settings
                  </Link>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Link href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471" target="_blank" rel="noopener">
                    Safari Cookie Settings
                  </Link>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Link href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener">
                    Edge Cookie Settings
                  </Link>
                }
              />
            </ListItem>
          </List>

          <SubSectionTitle>6.3 Mobile Device Settings</SubSectionTitle>
          <Typography variant="body1" paragraph>
            On mobile devices, you can typically manage cookies through your device's
            browser settings or system preferences.
          </Typography>

          {/* 7. Impact of Disabling Cookies */}
          <SectionTitle>7. Impact of Disabling Cookies</SectionTitle>
          <Typography variant="body1" paragraph>
            If you disable certain cookies, some features of the Service may not work
            as intended:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Essential cookies"
                secondary="Disabling these will prevent you from using the Service entirely, as authentication will not work."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Functional cookies"
                secondary="Your preferences (like theme settings) may not be remembered between sessions."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Analytics cookies"
                secondary="No impact on your experience, but it helps us improve the Service."
              />
            </ListItem>
          </List>

          {/* 8. Do Not Track Signals */}
          <SectionTitle>8. Do Not Track Signals</SectionTitle>
          <Typography variant="body1" paragraph>
            Some browsers have a "Do Not Track" (DNT) feature that sends a signal to
            websites requesting that your browsing not be tracked. Currently, there is
            no uniform standard for how to interpret DNT signals. We currently do not
            respond to DNT signals, but we limit tracking as described in this policy.
          </Typography>

          {/* 9. Updates to This Policy */}
          <SectionTitle>9. Updates to This Policy</SectionTitle>
          <Typography variant="body1" paragraph>
            We may update this Cookie Policy from time to time to reflect changes in our
            practices or for legal, technical, or regulatory reasons. When we make changes,
            we will update the "Last Updated" date at the top of this policy.
          </Typography>
          <Typography variant="body1" paragraph>
            For significant changes, we may provide additional notice, such as displaying
            a prominent notice on the Service or sending you an email notification.
          </Typography>

          {/* 10. Contact Us */}
          <SectionTitle>10. Contact Us</SectionTitle>
          <Typography variant="body1" paragraph>
            If you have questions about this Cookie Policy or our use of cookies, please
            contact us:
          </Typography>
          <Box sx={{ pl: 2, borderLeft: `3px solid ${theme.palette.primary.main}`, my: 2 }}>
            <Typography variant="body1">
              <strong>FamilyForLife</strong><br />
              Email: <Link href="mailto:privacy@familyforlife.app">privacy@familyforlife.app</Link>
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/terms" color="primary">
              Terms of Service
            </Link>
            <Link component={RouterLink} to="/privacy" color="primary">
              Privacy Policy
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CookiePolicy;
